import { useState } from "react";
import { useProjectContext } from "@contexts/projects";
import { formatNumberToLocale } from "@utils/formatNumber";
import CostOriginTitlesOffcanvas from "./CostOriginTitlesOffcanvas";
import { isPresentationProject } from "@utils/presentationMode";

// Indicador "origem do custo": divide o custo financeiro da obra (pago +
// a pagar) entre o que nasceu no módulo de suprimentos (títulos de pedido de compra
// e contrato) e o que foi lançado direto no contas a pagar. Não inclui os valores
// não lançados da Ampli. Lê activeProject.costOriginSplit (montado no getDetail).

const SUPPLIES_COLOR = "#1D9E75"; // teal
const FINANCE_COLOR = "#EF9F27"; // amber
const UNCLASSIFIED_COLOR = "#8C8C8C"; // gray
const DIVIDER = "1px solid rgba(0, 0, 0, 0.08)";

const formatPct = (value) =>
  (Number.isFinite(Number(value)) ? Number(value) : 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

const LegendRow = ({ color, label, value, pct, onClick }) => {
  const clickable = typeof onClick === "function";
  return (
    <div
      className="flex justify-content-space-between"
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      aria-label={clickable ? `Ver títulos ${label}` : undefined}
      style={{ alignItems: "center", padding: "8px 0", borderTop: DIVIDER, cursor: clickable ? "pointer" : "default" }}
    >
      <span className="flex" style={{ alignItems: "center", gap: 8 }}>
        <span style={{ width: 10, height: 10, borderRadius: 3, background: color, display: "inline-block", flexShrink: 0 }} />
        <span>{label}</span>
      </span>
      <span style={{ whiteSpace: "nowrap" }}>
        <span style={{ opacity: 0.6 }}>R$ {formatNumberToLocale(value)}</span>
        &nbsp;&nbsp;
        <strong>{formatPct(pct)}%</strong>
        {clickable && (
          <span aria-hidden style={{ marginLeft: 8, opacity: 0.5 }}>
            ›
          </span>
        )}
      </span>
    </div>
  );
};

const CostOriginSplitCard = () => {
  const { activeProject } = useProjectContext();
  const presentationReadOnly = isPresentationProject(activeProject);
  const [openBucket, setOpenBucket] = useState(null);
  const split = activeProject?.costOriginSplit;
  const projectId = activeProject?._id;

  // API ainda não deployada com o campo → não renderiza nada (degradação suave).
  if (!split) return null;

  const supplies = split.supplies ?? { value: 0, pct: 0 };
  const finance = split.finance ?? { value: 0, pct: 0 };
  const unclassified = typeof split.unclassified === "object"
    ? split.unclassified
    : { value: Number(split.unclassified) || 0, pct: 0 };
  const paid = split.paid ?? { supplies: 0, finance: 0, unclassified: 0 };
  const toPay = split.toPay ?? { supplies: 0, finance: 0, unclassified: 0 };
  const total = Number.isFinite(Number(split.titles_total))
    ? Number(split.titles_total)
    : supplies.value + finance.value + unclassified.value;
  const noTitles = total === 0;

  const tooltip =
    "Origem do custo da obra, sobre pago + a pagar: via suprimentos são os " +
    "títulos que nasceram de pedido de compra ou contrato (rastreáveis por fornecedor, " +
    "material e preço); via financeiro são os lançados direto no contas a pagar " +
    "(impostos, tarifas, financiamento e outros lançamentos diretos). Não classificado " +
    "é o valor cujo vínculo ainda não pôde ser determinado. " +
    `Pago: R$ ${formatNumberToLocale(paid.supplies)} suprimentos / R$ ${formatNumberToLocale(paid.finance)} financeiro / R$ ${formatNumberToLocale(paid.unclassified)} não classificado. ` +
    `A pagar: R$ ${formatNumberToLocale(toPay.supplies)} suprimentos / R$ ${formatNumberToLocale(toPay.finance)} financeiro / R$ ${formatNumberToLocale(toPay.unclassified)} não classificado. ` +
    "Não inclui os estouros / economias registrados na plataforma.";

  return (
    <>
      <div className="card" style={{ maxWidth: 520, marginTop: 24, marginBottom: 24 }}>
        <div className="flex justify-content-space-between">
          <span className="card-title">Origem do custo</span>
          <span
            title={tooltip}
            aria-label={tooltip}
            style={{
              cursor: "help",
              fontSize: 12,
              width: 16,
              height: 16,
              lineHeight: "16px",
              textAlign: "center",
              borderRadius: "50%",
              border: "1px solid rgba(0,0,0,0.25)",
              color: "rgba(0,0,0,0.5)",
              flexShrink: 0,
            }}
          >
            i
          </span>
        </div>

        {noTitles ? (
          <span className="card-label">Não há títulos para classificar nesta obra.</span>
        ) : (
          <>
            <div style={{ display: "flex", height: 12, borderRadius: 999, overflow: "hidden", margin: "14px 0 8px" }}>
              <div style={{ flexGrow: Math.max(0, supplies.value), flexBasis: 0, minWidth: supplies.value > 0 ? 5 : 0, background: SUPPLIES_COLOR }} />
              <div
                style={{
                  flexGrow: Math.max(0, finance.value),
                  flexBasis: 0,
                  minWidth: finance.value > 0 ? 5 : 0,
                  background: FINANCE_COLOR,
                }}
              />
              <div style={{ flexGrow: Math.max(0, unclassified.value), flexBasis: 0, minWidth: unclassified.value > 0 ? 5 : 0, background: UNCLASSIFIED_COLOR }} />
            </div>

            <LegendRow
              color={SUPPLIES_COLOR}
              label="Via suprimentos"
              value={supplies.value}
              pct={supplies.pct}
              onClick={supplies.value > 0 ? () => setOpenBucket("supplies") : undefined}
            />
            <LegendRow
              color={FINANCE_COLOR}
              label="Via financeiro"
              value={finance.value}
              pct={finance.pct}
              onClick={finance.value > 0 ? () => setOpenBucket("finance") : undefined}
            />
            <LegendRow
              color={UNCLASSIFIED_COLOR}
              label="Não classificado"
              value={unclassified.value}
              pct={unclassified.pct}
              onClick={unclassified.value > 0 ? () => setOpenBucket("unclassified") : undefined}
            />

            <div
              className="flex justify-content-space-between"
              style={{ alignItems: "center", paddingTop: 12, marginTop: 4, borderTop: DIVIDER }}
            >
              <span className="card-label">Pago + a pagar</span>
              <strong>R$ {formatNumberToLocale(total)}</strong>
            </div>

          </>
        )}
      </div>

      <CostOriginTitlesOffcanvas
        open={!!openBucket}
        bucket={openBucket}
        projectId={projectId}
        header={openBucket === "supplies" ? supplies : openBucket === "finance" ? finance : unclassified}
        readOnly={presentationReadOnly}
        onClose={() => setOpenBucket(null)}
      />
    </>
  );
};

export default CostOriginSplitCard;
